import { getCopilotChatDrawerState } from '../store/store';
import {
    DEFAULT_COPILOT_CHAT_DRAWER_WIDTH,
    NARROWEST_CANVAS_MODE_BREAKPOINT,
} from '../utils/copilotChatDrawerConstants';
import { getCopilotChatDrawerTrueWidth } from './getCopilotChatDrawerTrueWidth';
import { getCanvasContentMinimumWidth } from '../utils/getCanvasContentMinimumWidth';

/**
 * This function determines whether the Expand/Collapse button should be
 * shown in the Copilot Chat Drawer Header.
 *
 * We don't want to show Expand/Collapse when the Drawer cannot expand nor collapse at all.
 * This happens specifically at very narrow window sizes, where we're both unable to
 * expand due to being at our max-width and we're unable to collapse due to being at or below our min-width.
 *
 * With the nature of how this logic works, we just need to test if the drawer is below its minimum width,
 * as we always lose expansion capability first, and minimization capability last.
 *
 * Two edge cases:
 *  1. if we're in narrowest breakpoint mode, where drawer takes up the full space of the canvas, don't show.
 *  2. If we're specifically at 380px width and that is also exactly the max-width for the drawer, don't show.
 */
export function isExpandCollapseButtonShown(targetWindow: Window): boolean {
    const state = getCopilotChatDrawerState(targetWindow);
    if (!state) {
        return false;
    }

    const appCanvasWidth = state.appCanvasWidth;
    const drawerWidth = getCopilotChatDrawerTrueWidth(targetWindow);

    // Edge case: When drawer width is specifcally at 380px width, but that is also exactly the max-width
    // for the drawer, we should not show. We calculate this using similar logic from getCopilotChatDrawerTrueWidth.
    const thresholdCase =
        appCanvasWidth - getCanvasContentMinimumWidth() === DEFAULT_COPILOT_CHAT_DRAWER_WIDTH;

    return (
        !thresholdCase &&
        appCanvasWidth >= NARROWEST_CANVAS_MODE_BREAKPOINT &&
        drawerWidth >= DEFAULT_COPILOT_CHAT_DRAWER_WIDTH
    );
}
